import React, { useCallback, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { showError, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import { formatLocalDate } from "src/utils/date/dateUtils";

import {
	getTQC1040_R01List,
	getTQC1040_R01AllCount,
	saveTQC1040_R01,
	downloadReportPdf_TQC1040_R01,
	TQC1040_R01Params,
} from "./Api_TQC1040_R01";
import {
	defaultTQC1040_R01Dto,
	normalizeInspectValues,
	TQC1040_R01Dto,
} from "./Dto_TQC1040_R01";
import { useTQC1040_R01Store, TQC1040_R01Filter } from "./Store_TQC1040_R01";

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC1040_R01Dto> | null>;
}

export const Content_TQC1040_R01 = ({ gridRef }: Props) => {
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	const {
		TQC1040_R01Filter,
		setTQC1040_R01PastFilter,
		TQC1040_R01List,
		setTQC1040_R01List,
		setOriginalTQC1040_R01List,
		setSelectedTQC1040_R01List,
		setAllCount,
		setScrollTop,
		initData: initStoreData,
	} = useTQC1040_R01Store();

	const [clickable, setClickable] = useState(true);

	/** 그리드의 기본 행(빈 검사 행)을 생성한다. (그리드 렌더링/셀 계산용 기준값) */
	const defaultTQC1040_R01Row = useCallback(() => ({ ...defaultTQC1040_R01Dto }), []);

	/**
	 * 화면 필터를 서버 조회 파라미터로 변환한다.
	 * 빈 값은 undefined 로 만들어 쿼리스트링에서 제외하고, 입고기간 DateRange 는 yyyy-MM-dd 로 변환한다.
	 */
	const getParams = (filter: TQC1040_R01Filter): TQC1040_R01Params => ({
		compCd: filter.compCd || undefined,
		equipmentType: filter.equipmentType || undefined,
		equipmentCd: filter.equipmentCd || undefined,
		startDate: filter.inspectDt?.from ? formatLocalDate(filter.inspectDt.from) : undefined,
		endDate: filter.inspectDt?.to ? formatLocalDate(filter.inspectDt.to) : undefined,
	});

	// ── 조회 ─────────────────────────────────────────────────
	/**
	 * 조회 버튼 처리. 조회조건으로 목록/전체건수를 가져와 스토어에 채운다.
	 * 빈 검사값은 'N'으로 정규화하며, 목록과 원본에 동일한 값을 넣어
	 * 조회 직후 불필요한 수정표시(isModified)가 생기지 않게 한다.
	 */
	const fetchData = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			setScrollTop(null);

			const params = getParams(TQC1040_R01Filter);
			const response = await getTQC1040_R01List("search", params);

			// 빈 검사값은 기본 'N'으로 채워 목록과 원본에 동일하게 반영한다.
			const data = (response.data.data ?? []).map((item, idx) =>
				normalizeInspectValues({
					...defaultTQC1040_R01Dto,
					...item,
					valueIndex: idx + 1,
					inserted: false,
					isModified: false,
				}),
			);

			setTQC1040_R01List(data);
			setOriginalTQC1040_R01List(data);
			setSelectedTQC1040_R01List([]);

			const countResponse = await getTQC1040_R01AllCount(params);
			setAllCount(countResponse.data.data ?? data.length);
			setTQC1040_R01PastFilter(TQC1040_R01Filter);

			data.length > 0
				? showSuccess(`조회 완료 (${data.length}건)`)
				: showWarning("조회된 데이터가 없습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC1040_R01Filter, setLoadingSpinner, setScrollTop,
		setTQC1040_R01List, setOriginalTQC1040_R01List, setSelectedTQC1040_R01List,
		setAllCount, setTQC1040_R01PastFilter,
	]);

	// ── 저장 (수정된 행만 UPDATE) ────────────────────────────
	/**
	 * 저장 버튼 처리. isModified 인 행만 추려 서버로 UPDATE 요청한다.
	 * 변경 행이 없으면 경고 후 종료하고, 저장 성공 시 동일 조건으로 재조회해 목록/원본을 최신화한다.
	 */
	const handleSaveClick = useCallback(async () => {
		const modifiedRows = TQC1040_R01List.filter((row) => row.isModified);

		if (modifiedRows.length === 0) {
			showWarning("변경된 데이터가 없습니다.");
			return;
		}

		try {
			setLoadingSpinner(true);
			const saveResponse = await saveTQC1040_R01(modifiedRows);

			// 인터셉터가 4xx/5xx 응답도 resolve 로 돌려주므로 successFlag 로 성공 여부를 판단한다.
			// (실패 사유는 인터셉터가 이미 토스트로 표시하므로 여기서는 성공 처리만 중단한다)
			if (!saveResponse?.data?.successFlag) {
				return;
			}

			// 저장 후 재조회
			const params = getParams(TQC1040_R01Filter);
			const response = await getTQC1040_R01List("search", params);
			const data = (response.data.data ?? []).map((item, idx) =>
				normalizeInspectValues({
					...defaultTQC1040_R01Dto,
					...item,
					valueIndex: idx + 1,
					inserted: false,
					isModified: false,
				}),
			);
			setTQC1040_R01List(data);
			setOriginalTQC1040_R01List(data);
			setSelectedTQC1040_R01List([]);

			showSuccess("저장이 완료되었습니다.");
		} catch {
			showError("저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC1040_R01List, TQC1040_R01Filter, setLoadingSpinner,
		setTQC1040_R01List, setOriginalTQC1040_R01List, setSelectedTQC1040_R01List,
	]);

	// ── 출력 (약품 수입 검사대장 PDF) ────────────────────────
	/**
	 * 출력 버튼 처리. 현재 조회조건으로 검사대장 PDF(Blob)를 받아 새 탭으로 연다.
	 * 열람 후 5초 뒤 Object URL 을 해제해 메모리 누수를 막는다.
	 */
	const handlePrintClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadReportPdf_TQC1040_R01(getParams(TQC1040_R01Filter));
			const blob = new Blob([response.data], { type: "application/pdf" });
			const fileUrl = URL.createObjectURL(blob);
			window.open(fileUrl, "_blank");
			setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
		} catch {
			showError("출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC1040_R01Filter, setLoadingSpinner]);

	// ── 초기화 ───────────────────────────────────────────────
	/** 초기화 버튼 처리. 스토어(필터/목록/선택 등)를 초기화하고 그리드도 비운다. */
	const initContainerData = useCallback(() => {
		initStoreData();
		gridRef.current?.api?.deselectAll();
		gridRef.current?.api?.setGridOption("rowData", []);
	}, [gridRef, initStoreData]);

	return {
		clickable, setClickable,
		defaultTQC1040_R01Row,
		fetchData,
		handleSaveClick,
		handlePrintClick,
		initContainerData,
	};
};
